import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import { Loader2, AlertCircle, RefreshCw, Inbox, ChevronDown, Mail, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['new', 'read', 'responded'];
const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  read: 'bg-gray-100 text-gray-600 border-gray-200',
  responded: 'bg-green-100 text-green-700 border-green-200',
};

export default function AdminEnquiries() {
  const { authHeaders } = useAdminAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/enquiries`, { headers: authHeaders });
      setEnquiries(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load enquiries.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await axios.put(`${API}/admin/enquiries/${id}`, { status }, { headers: authHeaders });
      setEnquiries(prev => prev.map(e => e.id === id ? { ...e, status } : e));
    } catch (e) {
      alert('Update failed.');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = filter === 'all' ? enquiries : enquiries.filter(e => e.status === filter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: enquiries.filter(e => e.status === s).length }), {});

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Enquiries</h1>
            <p className="text-gray-500 text-sm">{enquiries.length} total enquiries</p>
          </div>
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          <FilterTab label="All" count={enquiries.length} active={filter === 'all'} onClick={() => setFilter('all')} />
          {STATUS_OPTIONS.map(s => (
            <FilterTab key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} count={counts[s]} active={filter === s} onClick={() => setFilter(s)} />
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Inbox size={32} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{error ? 'Configure Supabase to store enquiries.' : 'No enquiries in this category.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(enq => (
              <div key={enq.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Header row */}
                <button
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
                  onClick={() => setExpanded(expanded === enq.id ? null : enq.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">
                        {enq.first_name} {enq.last_name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${STATUS_COLORS[enq.status] || STATUS_COLORS.new}`}>
                        {enq.status}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 capitalize`}>
                        {enq.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-500 flex items-center gap-1"><Mail size={11} />{enq.email}</span>
                      {enq.phone && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{enq.phone}</span>}
                      {enq.course_title && <span className="text-xs text-blue-600">{enq.course_title}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(enq.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform shrink-0 ${
                    expanded === enq.id ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Expanded detail */}
                {expanded === enq.id && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50 space-y-4">
                    {enq.subject && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Subject</p>
                        <p className="text-sm text-gray-800">{enq.subject}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Message</p>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{enq.message}</p>
                    </div>
                    {enq.enrolment_data && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Application Details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 bg-white border border-gray-100 rounded-lg p-3">
                          {Object.entries(enq.enrolment_data)
                            .filter(([, v]) => v !== null && v !== '' && v !== false)
                            .map(([k, v]) => (
                              <div key={k} className="flex gap-2 text-xs">
                                <span className="text-gray-500 capitalize min-w-[140px]">{k.replace(/_/g, ' ')}:</span>
                                <span className="text-gray-800 break-all">{String(v)}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 pt-2 flex-wrap">
                      <p className="text-xs text-gray-500 font-medium">Update status:</p>
                      {STATUS_OPTIONS.map(s => (
                        <button
                          key={s}
                          disabled={enq.status === s || updating === enq.id}
                          onClick={() => updateStatus(enq.id, s)}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium capitalize transition-colors disabled:opacity-50 ${
                            enq.status === s
                              ? STATUS_COLORS[s]
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {updating === enq.id ? <Loader2 size={10} className="animate-spin inline" /> : s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function FilterTab({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
        active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
      }`}
    >
      {label}
      <span className={`text-xs ${
        active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
      } px-1.5 py-0.5 rounded-full`}>{count}</span>
    </button>
  );
}
